import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { TFunction } from "i18next";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalize a Kuwaiti phone typed/autofilled into an input that already
 * shows a fixed +965 prefix. Autofill bypasses maxLength and inserts the
 * full international number (e.g. "+965 9967 3276" or "0096599673276"),
 * which would otherwise get +965 prepended twice. Returns local digits only,
 * capped at 8.
 */
export function normalizeKuwaitPhoneInput(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  while (digits.length > 8 && digits.startsWith("965")) digits = digits.slice(3);
  return digits.slice(0, 8);
}

/**
 * Build a localized address string from an order's individual fields.
 * Falls back to the stored `customerAddress` string when fields are missing
 * (e.g. legacy orders or custom-formatted addresses).
 */
export function formatOrderAddress(
  order: { block?: string; street?: string; avenue?: string; building?: string; landmark?: string; customerAddress?: string },
  t: TFunction,
): string {
  const hasFields = order.block || order.street || order.building;
  if (!hasFields) return order.customerAddress || "";

  const parts: string[] = [];
  if (order.block) parts.push(`${t("addresses.block")} ${order.block}`);
  if (order.street) parts.push(`${t("addresses.street")} ${order.street}`);
  if (order.avenue) parts.push(`${t("addresses.avenue")} ${order.avenue}`);
  if (order.building) parts.push(`${t("addresses.building")} ${order.building}`);
  if (order.landmark) parts.push(order.landmark);
  return parts.join(", ");
}

/**
 * Trigger a file download from a Blob, works in PWAs and in-app browsers.
 *
 * iOS PWA webviews ignore the `download` attribute on blob: URLs, so we
 * convert the blob to a data: URL (base64) which bypasses that restriction.
 * On desktop/Android the blob: URL path is used (faster, less memory).
 */
export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const isIOSPWA =
    typeof window !== "undefined" &&
    // iOS standalone mode = launched from home screen (PWA)
    (window.navigator as any).standalone === true;

  let href: string;
  let isObjectUrl = false;

  if (isIOSPWA) {
    // Convert to base64 data URL — reliably downloadable in iOS PWA webview.
    href = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } else {
    href = window.URL.createObjectURL(blob);
    isObjectUrl = true;
  }

  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (isObjectUrl) {
    // Defer revoke so the click has time to start the download.
    setTimeout(() => window.URL.revokeObjectURL(href), 1000);
  }
}

